// EAA-1667: Moved from packages/ui/src/auth/useAuthDecision.ts into packages/hooks
// with the rest of the capability seam. Pure decision logic (no rendering); the
// presentational HOC withAuthGuard stays in packages/ui and consumes this from here.
import { useMemo } from 'react';

import type { AuthFallback, AuthProps } from './AuthProps';
import { useAuthCapability } from './AuthCapabilityContext';

export type AuthDecision = 'allow' | AuthFallback;

/**
 * Shared decision logic behind `withAuthGuard` — extracted so components
 * that must stay generic over a type parameter (e.g. `Select<ValueType>`,
 * which a `forwardRef`-based HOC would collapse to a single concrete
 * instantiation) can apply the same `auth`/`authFallback` contract by hand
 * without duplicating the capability-check logic.
 */
export function useAuthDecision(auth: AuthProps['auth'], fallback: AuthFallback): AuthDecision {
    const { can } = useAuthCapability();

    return useMemo(() => {
        if (!auth) return 'allow';
        const keys = Array.isArray(auth) ? auth : [auth];
        // EAA-1666: `[].every(...)` is vacuously true — an empty `auth` array
        // must not silently allow (fail closed, same stance as the context
        // default), so treat it as an explicit denial instead.
        if (keys.length === 0) return fallback;
        const allowed = keys.every((key) => can(key));
        return allowed ? 'allow' : fallback;
    }, [auth, fallback, can]);
}
